import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { C, card, label, btn, input } from "../lib/theme";

const STATUS_COLOR = { pending: "brass", completed: "earth", revoked: "faint" };

export default function IntakeLinks({ me }) {
  const [templates, setTemplates] = useState([]);
  const [links, setLinks] = useState([]);
  const [charts, setCharts] = useState([]);
  const [form, setForm] = useState({ template: "", title: "", chart_id: "", expires_days: 30 });
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const isPro = me?.plan === "professional";

  const refreshLinks = () => api.intakeLinks().then(setLinks).catch(() => {});
  useEffect(() => {
    if (!isPro) return;
    api.intakeTemplates().then((t) => {
      setTemplates(t);
      setForm((f) => ({ ...f, template: f.template || (t[0]?.id ?? "") }));
    }).catch((e) => setErr(e.message));
    refreshLinks();
    api.charts().then(setCharts).catch(() => {});
  }, [isPro]);

  if (!isPro) return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ ...card, color: C.dim, fontSize: 13, lineHeight: 1.7 }}>
        Client intake links are a <b style={{ color: C.text }}>Professional plan</b> feature.
        They let you send a client a private, tokenized questionnaire; their answers fill the
        client chart's event history <i>blind</i> — the client never sees the astrology.
      </div>
    </div>
  );

  const selected = templates.find((t) => t.id === form.template);
  const fullUrl = (u) => (typeof window !== "undefined" ? window.location.origin : "") + u;
  const copy = (text, key) => {
    try { navigator.clipboard.writeText(text); setCopied(key);
      setTimeout(() => setCopied(null), 1800); } catch { /* clipboard unavailable */ }
  };

  const create = async () => {
    setBusy(true); setErr(""); setCreated(null);
    try {
      const body = { template: form.template, expires_days: Number(form.expires_days) };
      if (form.title.trim()) body.title = form.title.trim();
      if (form.chart_id) body.chart_id = form.chart_id;
      const res = await api.createIntakeLink(body);
      setCreated(res);
      setForm((f) => ({ ...f, title: "" }));
      refreshLinks();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ fontFamily: C.displayFont, fontSize: 26, marginBottom: 2 }}>Client intake</div>
      <div style={{ fontSize: 11.5, color: C.faint, marginBottom: 16 }}>
        Send a client a private questionnaire. Answers arrive blind — timed events against their
        chart, never shown to the client.
      </div>

      {/* ---- new link ---- */}
      <div style={{ ...card, marginBottom: 18 }}>
        <div style={label}>NEW QUESTIONNAIRE</div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={fieldLabel}>Template</div>
            <select value={form.template} style={{ ...input, minWidth: 220 }}
              onChange={(e) => setForm({ ...form, template: e.target.value })}>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title} ({t.question_count} question{t.question_count === 1 ? "" : "s"})
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={fieldLabel}>Attach to chart</div>
            <select value={form.chart_id} style={{ ...input, minWidth: 200 }}
              onChange={(e) => setForm({ ...form, chart_id: e.target.value })}>
              <option value="">Client provides birth details</option>
              {charts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}{c.is_self ? " (mine)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={fieldLabel}>Expires (days)</div>
            <input type="number" min="1" max="180" value={form.expires_days}
              style={{ ...input, width: 90 }}
              onChange={(e) => setForm({ ...form, expires_days: e.target.value })} />
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <div style={fieldLabel}>Title (optional — overrides the template's)</div>
          <input value={form.title} placeholder={selected?.title || "Questionnaire title"}
            style={{ ...input, width: "100%", boxSizing: "border-box" }}
            onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>

        {selected?.instructions && (
          <div style={{ fontSize: 11.5, color: C.dim, lineHeight: 1.6, margin: "10px 0 0",
            paddingLeft: 10, borderLeft: `2px solid ${C.line}` }}>
            {selected.instructions}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 14 }}>
          <button style={btn()} disabled={busy || !form.template} onClick={create}>
            {busy ? "Creating…" : "Create link"}
          </button>
          <span style={{ fontSize: 11, color: C.faint }}>
            Single use · client sees questions only, never the periods (blind by design).
          </span>
        </div>
        {err && <div style={{ color: C.rose, fontSize: 12, marginTop: 10 }}>{err}</div>}

        {created && (
          <div style={{ ...card, background: C.surface2, borderColor: C.brass, marginTop: 14 }}>
            <div style={{ fontSize: 12, color: C.brass, marginBottom: 6 }}>Link created — send this to your client:</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <code style={{ fontFamily: "ui-monospace, monospace", fontSize: 12.5,
                color: C.text, wordBreak: "break-all", flexGrow: 1 }}>
                {fullUrl(created.url)}
              </code>
              <button style={btn(false)} onClick={() => copy(fullUrl(created.url), "new")}>
                {copied === "new" ? "Copied ✓" : "Copy"}
              </button>
            </div>
            <div style={{ fontSize: 10.5, color: C.faint, marginTop: 6 }}>
              Expires {String(created.expires_at).slice(0, 10)}. {created.note}
            </div>
          </div>
        )}
      </div>

      {/* ---- client charts ---- */}
      {charts.filter((ch) => !ch.is_self && ch.interactive).length > 0 && <>
        <div style={label}>YOUR CLIENTS</div>
        {charts.filter((ch) => !ch.is_self && ch.interactive).map((ch) => (
          <Link key={ch.id} to={`/clients/${ch.id}`} style={{ textDecoration: "none",
            color: "inherit" }}>
            <div style={{ ...card, padding: "10px 16px", marginBottom: 8,
              display: "flex", gap: 10, alignItems: "baseline", cursor: "pointer" }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{ch.label}</span>
              <span style={{ fontSize: 11.5, color: C.dim }}>
                {ch.asc_sign} rising · {ch.sect} chart</span>
              <span style={{ marginLeft: "auto", fontSize: 11.5, color: C.brass }}>
                Session prep →</span>
            </div>
          </Link>
        ))}
        <div style={{ height: 10 }} />
      </>}

      {/* ---- existing links ---- */}
      <div style={label}>YOUR LINKS</div>
      {links.length === 0 && (
        <div style={{ ...card, fontSize: 12.5, color: C.faint }}>No intake links yet.</div>
      )}
      {links.map((l) => (
        <div key={l.id} style={{ ...card, padding: "12px 16px", marginBottom: 8 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{l.title}</span>
            <span style={{ fontSize: 9.5, letterSpacing: "0.1em", textTransform: "uppercase",
              color: C[STATUS_COLOR[l.status]] || C.dim,
              border: `1px solid ${C[STATUS_COLOR[l.status]] || C.line}`,
              borderRadius: 3, padding: "1px 6px" }}>{l.status}</span>
            <span style={{ marginLeft: "auto", fontSize: 10.5, color: C.faint }}>
              created {String(l.created_at).slice(0, 10)} · expires {String(l.expires_at).slice(0, 10)}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
            <code style={{ fontFamily: "ui-monospace, monospace", fontSize: 11.5, color: C.dim,
              wordBreak: "break-all", flexGrow: 1 }}>{fullUrl(`/intake/${l.id}`)}</code>
            {l.status === "pending" && (
              <>
                <button style={btn(false)} onClick={() => copy(fullUrl(`/intake/${l.id}`), l.id)}>
                  {copied === l.id ? "Copied ✓" : "Copy"}
                </button>
                <button
                  style={{ ...btn(false), color: C.rose, borderColor: C.rose }}
                  onClick={() => api.revokeIntakeLink(l.id).then(refreshLinks).catch((e) => setErr(e.message))}>
                  Revoke
                </button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

const fieldLabel = { fontSize: 10, letterSpacing: "0.08em", marginBottom: 4,
  get color() { return C.faint; } };
